import { computed, customRef, type ComputedRef, type Ref } from "vue";
import { useComponentContext } from "composables/useComponentContext";
import { useStateStore } from "composables/useStateStore";
import type { MenuProps, MenuItemProps } from "./menu.types";

const MENU_TYPE = "Menu";
const MENU_ITEM_TYPE = "MenuItem";

export interface UseMenuCoordinationReturn {
  menuProps: ComputedRef<(MenuItemProps | MenuProps<MenuItemProps>) | undefined>;
  isSubItem: ComputedRef<boolean>;
  currentLevel: Ref<number>;
  isChild: ComputedRef<boolean>;
  isOpen: ComputedRef<boolean>;
  hasChildren: Ref<boolean>;
  isExpandable: ComputedRef<boolean>;
  items: ComputedRef<unknown[]>;
  menuUid: ComputedRef<number | null | undefined>;
  open: (itemUid?: number) => void;
  close: (itemUid?: number) => void;
  toggle: (itemUid?: number) => void;
  closeOthers: () => void;
}

type MenuContext = ReturnType<typeof useComponentContext<MenuItemProps | MenuProps<MenuItemProps>>>;
type MenuStateStore = ReturnType<typeof useStateStore<MenuItemProps | MenuProps<MenuItemProps>>>;

/** Cached customRef for menu item nesting level. */
const buildCurrentLevel = (context: MenuContext, stateStore: MenuStateStore): Ref<number> => {
  let cachedLevel: number | null = null;
  let lastVersion = 0;
  return customRef((track) => ({
    get() {
      track();
      const ver = stateStore.getVersion();
      if (cachedLevel !== null && lastVersion === ver) return cachedLevel;
      lastVersion = ver;
      if (!context.uid || context.typeName !== MENU_ITEM_TYPE) {
        cachedLevel = 0;
        return 0;
      }
      let level = 0;
      let uid: number = context.uid;
      let parent = stateStore.findParent<MenuProps<MenuItemProps> | MenuItemProps>(uid);
      while (parent) {
        if (parent.type === MENU_TYPE) break;
        if (parent.type === MENU_ITEM_TYPE) level++;
        if (!parent.uid) break;
        uid = parent.uid;
        parent = stateStore.findParent<MenuProps<MenuItemProps> | MenuItemProps>(uid);
      }
      cachedLevel = level;
      return level;
    },
    set() {},
  }));
};

/** Cached customRef for whether a menu item has children. */
const buildHasChildren = (context: MenuContext, stateStore: MenuStateStore): Ref<boolean> => {
  let cached: boolean | null = null;
  let lastVersion = 0;
  return customRef((track) => ({
    get() {
      track();
      if (context.typeName !== MENU_ITEM_TYPE || !context.uid) return false;
      const ver = stateStore.getVersion();
      if (cached !== null && lastVersion === ver) return cached;
      lastVersion = ver;
      const children = stateStore.findChildrenByType<MenuItemProps>(context.uid, MENU_ITEM_TYPE);
      cached = children && children.length > 0;
      return cached;
    },
    set() {},
  }));
};

/** Resolve item UID from explicit arg or component context. */
const resolveMenuItemUid = (context: MenuContext, stateStore: MenuStateStore, itemUid?: number): number => {
  if (itemUid !== undefined) return itemUid;
  if (!context.uid) throw new Error("No itemUid provided and no context.uid available.");
  if (context.typeName === MENU_ITEM_TYPE) return context.uid;
  const parentItem = stateStore.findParentOfType<MenuItemProps>(context.uid, MENU_ITEM_TYPE);
  if (parentItem?.uid) return parentItem.uid;
  throw new Error("No itemUid provided and not called from or inside a MenuItem component.");
};

/** Build open/close/toggle/closeOthers operations for menu items. */
const buildMenuItemOps = (
  context: MenuContext,
  stateStore: MenuStateStore,
  menuUid: ComputedRef<number | null | undefined>,
) => {
  const resolve = (uid?: number) => resolveMenuItemUid(context, stateStore, uid);
  return {
    open: (uid?: number) => {
      stateStore.setValue<MenuItemProps>(resolve(uid), "open", true);
    },
    close: (uid?: number) => {
      stateStore.setValue<MenuItemProps>(resolve(uid), "open", false);
    },
    toggle: (uid?: number) => {
      const id = resolve(uid);
      const item = stateStore.getByUid<MenuItemProps>(id);
      if (item) stateStore.setValue<MenuItemProps>(id, "open", !item.props.open);
    },
    closeOthers: () => {
      if (!context.uid || !menuUid.value) return;
      for (const item of stateStore.findChildrenByType<MenuItemProps>(menuUid.value, MENU_ITEM_TYPE)) {
        if (item.uid == null || item.uid === context.uid || !item.props?.open) continue;
        stateStore.setValue<MenuItemProps>(item.uid, "open", false);
      }
    },
  };
};

/**
 * Composable for MenuItem and other Menu child slots.
 *
 * Reads the parent Menu's state via the cross-CE registration handshake and
 * exposes coordination data: nesting level, child detection, sibling op
 * helpers (open/close/toggle/closeOthers).
 */
export const useMenuCoordination = (): UseMenuCoordinationReturn => {
  const context = useComponentContext<MenuItemProps | MenuProps<MenuItemProps>>();
  const stateStore = useStateStore<MenuItemProps | MenuProps<MenuItemProps>>();
  const menuUid = computed(() => {
    if (context.typeName === MENU_TYPE) return context.uid;
    return stateStore.findParentOfType<MenuProps<MenuItemProps>>(context.uid as number, MENU_TYPE)?.uid;
  });
  const items = computed(() =>
    menuUid.value ? stateStore.findChildrenByType<MenuItemProps>(menuUid.value, MENU_ITEM_TYPE) : [],
  );
  const menuProps = computed(() => {
    if (!context.uid) return;
    return stateStore.findParentOfType<MenuProps<MenuItemProps>>(context.uid, MENU_TYPE)?.props;
  });
  const isSubItem = computed(() => {
    if (!context.uid || context.typeName !== MENU_ITEM_TYPE) return false;
    return stateStore.findParent<MenuProps<MenuItemProps> | MenuItemProps>(context.uid)?.type === MENU_TYPE;
  });
  const currentLevel = buildCurrentLevel(context, stateStore);
  const hasChildren = buildHasChildren(context, stateStore);
  const { open, close, toggle, closeOthers } = buildMenuItemOps(context, stateStore, menuUid);
  const isOpen = computed(() => {
    if (context.typeName !== MENU_ITEM_TYPE || !context.uid) return false;
    const item = stateStore.getByUid<MenuItemProps>(context.uid);
    return item ? !!item.props.open : false;
  });
  const isChild = computed(() => currentLevel.value === 1);
  const isExpandable = computed(() => hasChildren.value && currentLevel.value === 0);
  return {
    menuProps,
    isSubItem,
    currentLevel,
    isChild,
    isOpen,
    hasChildren,
    isExpandable,
    items,
    menuUid,
    open,
    close,
    toggle,
    closeOthers,
  };
};
